import React from 'react';
import PropTypes from 'prop-types';
import { Box, useTheme, Chip, Tooltip, CircularProgress } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { WalteroCard, WalteroTypography, WalteroChart } from '../waltero';
import { formatDeviceCount, formatPercentage, formatRSSI } from '../../utils/performanceFormatters';
import { hasOrganizationAccess } from '../../utils/auth';
import { ADMIN_ORGANIZATION_ID } from '../../config/api';
import { useResponsive } from '../../hooks/useResponsive';

const PerformanceSummaryCards = ({ fleetStats, performanceData }) => {
  const theme = useTheme();
  const { breakpoints } = useResponsive();
  const isAdmin = hasOrganizationAccess(ADMIN_ORGANIZATION_ID);

  if (!fleetStats || Object.keys(fleetStats).length === 0) {
    return null;
  }

  // Helper function to safely get numeric values
  const getNumericValue = (value, defaultValue = 0) => {
    if (value === null || value === undefined || isNaN(value)) {
      return defaultValue;
    }
    return parseFloat(value);
  };

  // Special function for battery gauge showing voltage in center but percentage on gauge
  const createBatteryGaugeOption = (healthPercentage, averageVoltage, colorRanges) => {
    const percentageValue = getNumericValue(healthPercentage, 0);
    const voltageValue = getNumericValue(averageVoltage, 0);
    const isNoData = healthPercentage === null || healthPercentage === undefined || isNaN(healthPercentage);
    
    return {
      series: [{
        type: 'gauge',
        center: ['50%', '65%'],
        radius: breakpoints.isMobile ? '90%' : '95%',
        min: 0,
        max: 100,
        startAngle: 200,
        endAngle: -20,
        splitNumber: 5,
        itemStyle: {
          color: isNoData ? theme.palette.grey[300] : colorRanges.find(range => 
            percentageValue >= range.min && percentageValue < range.max
          )?.color || colorRanges[colorRanges.length - 1].color
        },
        progress: {
          show: true,
          width: breakpoints.isMobile ? 8 : 12,
          roundCap: true
        },
        pointer: {
          show: false
        },
        axisLine: {
          lineStyle: {
            width: breakpoints.isMobile ? 8 : 12,
            color: [
              [1, theme.palette.grey[200]]
            ]
          }
        },
        axisTick: {
          show: false
        },
        splitLine: {
          show: false
        },
        axisLabel: {
          show: false
        },
        detail: {
          show: true,
          fontSize: breakpoints.isMobile ? 20 : 24,
          fontWeight: 'bold',
          fontFamily: '"Mozaic Geo", "Inter", sans-serif',
          color: theme.palette.waltero.primary.coreBlue,
          formatter: isNoData ? 'N/A' : `${voltageValue.toFixed(1)}V`,
          offsetCenter: [0, '15%']
        },
        title: {
          show: false // Remove title from gauge
        },
        data: [
          {
            value: percentageValue
          }
        ],
        animation: true,
        animationDuration: 1000,
        animationEasing: 'elasticOut'
      }]
    };
  };

  // Helper function to create gauge option
  const createGaugeOption = (value, unit, colorRanges, min = 0, max = 100) => {
    const numericValue = getNumericValue(value, 0);
    const displayValue = value === null || value === undefined || isNaN(value) ? 0 : numericValue;
    const isNoData = value === null || value === undefined || isNaN(value);
    
    return {
      series: [{
        type: 'gauge',
        center: ['50%', '65%'],
        radius: breakpoints.isMobile ? '90%' : '95%',
        min,
        max,
        startAngle: 200,
        endAngle: -20,
        splitNumber: 5,
        itemStyle: {
          color: isNoData ? theme.palette.grey[300] : colorRanges.find(range => 
            displayValue >= range.min && displayValue < range.max
          )?.color || colorRanges[colorRanges.length - 1].color
        },
        progress: {
          show: true,
          width: breakpoints.isMobile ? 8 : 12,
          roundCap: true
        },
        pointer: {
          show: false
        },
        axisLine: {
          lineStyle: {
            width: breakpoints.isMobile ? 8 : 12,
            color: [
              [1, theme.palette.grey[200]]
            ]
          }
        },
        axisTick: {
          show: false
        },
        splitLine: {
          show: false
        },
        axisLabel: {
          show: false
        },
        detail: {
          show: true,
          fontSize: breakpoints.isMobile ? 20 : 24,
          fontWeight: 'bold',
          fontFamily: '"Mozaic Geo", "Inter", sans-serif',
          color: theme.palette.waltero.primary.coreBlue,
          formatter: isNoData ? 'N/A' : `{value}${unit}`,
          offsetCenter: [0, '15%']
        },
        title: {
          show: false // Remove title from gauge
        },
        data: [
          {
            value: displayValue
          }
        ],
        animation: true,
        animationDuration: 1000,
        animationEasing: 'elasticOut'
      }]
    };
  };

  // Color ranges for different metrics
  const successRateColorRanges = [
    { min: 0, max: 50, color: theme.palette.waltero.fleetStatus.critical }, // Red
    { min: 50, max: 80, color: theme.palette.waltero.fleetStatus.warning }, // Orange/Yellow
    { min: 80, max: 100, color: theme.palette.waltero.fleetStatus.excellent }  // Green (Apricot in Waltero)
  ];

  const rssiColorRanges = [
    { min: 0, max: 12, color: theme.palette.waltero.fleetStatus.critical },   // Red: 0-12 (poor signal)
    { min: 12, max: 24, color: theme.palette.waltero.fleetStatus.warning },  // Orange/Yellow: 12-24 (moderate signal)
    { min: 24, max: 31, color: theme.palette.waltero.fleetStatus.excellent } // Green: 24-31 (excellent signal)
  ];

  const batteryColorRanges = [
    { min: 0, max: 30, color: theme.palette.waltero.fleetStatus.critical },
    { min: 30, max: 70, color: theme.palette.waltero.fleetStatus.warning },
    { min: 70, max: 100, color: theme.palette.waltero.fleetStatus.excellent }
  ];


  // Get connection percentage data from fleetStats (calculated from SharedPerformanceCache)
  const getConnectionPercentages = () => {
    return {
      averageConnectionPercentage: fleetStats?.averageConnectionPercentage || 0,
      connectionPercentages: fleetStats?.connectionPercentages || []
    };
  };

  // Calculate distribution data for histograms from fleetStats arrays
  const calculateDistributionData = (dataType) => {
    if (!fleetStats) {
      return null;
    }

    let values = [];
    
    switch (dataType) {
      case 'successRate':
        values = fleetStats.successRates || [];
        break;
      case 'rssi':
        values = fleetStats.rssiValues || [];
        break;
      case 'battery':
        values = fleetStats.batteryVoltages || [];
        break;
      case 'connectionPercentage':
        values = fleetStats.connectionPercentages || [];
        break;
    }

    if (values.length === 0) {
      return null;
    }

    // Create histogram bins
    const min = Math.min(...values);
    const max = Math.max(...values);
    const binCount = 8;
    const binSize = (max - min) / binCount;
    
    // Handle single-value case (min === max)
    if (min === max) {
      return [{
        min: min - 0.5,
        max: max + 0.5, 
        count: values.length,
        label: dataType === 'successRate' ? `${Math.round(min)}%` :
               dataType === 'rssi' ? `${Math.round(min * 10) / 10}` :
               dataType === 'battery' ? `${min.toFixed(1)}V` :
               dataType === 'connectionPercentage' ? `${Math.round(min)}%` :
               `${min}`
      }];
    }
    
    // Validate binning parameters
    if (!Number.isFinite(min) || !Number.isFinite(max) || !Number.isFinite(binSize) || binSize <= 0) {
      return null;
    }
    
    const bins = [];
    for (let i = 0; i < binCount; i++) {
      const binMin = min + i * binSize;
      const binMax = i === binCount - 1 ? max : min + (i + 1) * binSize;
      const count = values.filter(value => value >= binMin && (i === binCount - 1 ? value <= binMax : value < binMax)).length;
      
      bins.push({
        min: binMin,
        max: binMax,
        count: count,
        label: ''
      });
    }

    // Format labels based on data type
    bins.forEach(bin => {
      switch (dataType) {
        case 'successRate':
          bin.label = `${Math.round(bin.min)}-${Math.round(bin.max)}%`;
          break;
        case 'rssi':
          bin.label = `${Math.round(bin.min * 10) / 10}-${Math.round(bin.max * 10) / 10}`;
          break;
        case 'battery':
          bin.label = `${bin.min.toFixed(1)}-${bin.max.toFixed(1)}V`;
          break;
        case 'connectionPercentage':
          bin.label = `${Math.round(bin.min)}-${Math.round(bin.max)}%`;
          break;
      }
    });

    return bins;
  };

  // Create histogram chart option
  const createHistogramOption = (distributionData, title, color) => {
    if (!distributionData) {
      return {
        xAxis: { type: 'category', data: ['No Data'] },
        yAxis: { type: 'value' },
        series: [{ type: 'bar', data: [0] }]
      };
    }

    return {
      title: title ? {
        text: title,
        left: 'center',
        top: 0,
        textStyle: {
          fontSize: 12,
          color: theme.palette.waltero.primary.mediumBlue,
          fontFamily: '"Inter", sans-serif'
        }
      } : { show: false },
      grid: {
        left: '5%',
        right: '5%',
        bottom: '15%',
        top: title ? '20%' : '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: distributionData.map(bin => bin.label),
        axisLabel: {
          fontSize: breakpoints.isMobile ? 10 : 11,
          color: theme.palette.waltero.primary.mediumBlue,
          rotate: breakpoints.isMobile ? 45 : 0,
          fontFamily: '"Inter", sans-serif'
        },
        axisLine: {
          lineStyle: {
            color: theme.palette.grey[300]
          }
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          fontSize: breakpoints.isMobile ? 10 : 11,
          color: theme.palette.waltero.primary.mediumBlue,
          fontFamily: '"Inter", sans-serif'
        },
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        splitLine: {
          lineStyle: {
            color: theme.palette.grey[200]
          }
        }
      },
      series: [{
        type: 'bar',
        data: distributionData.map(bin => bin.count),
        itemStyle: {
          color: color,
          borderRadius: [2, 2, 0, 0]
        },
        barWidth: '60%'
      }],
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const dataIndex = params[0].dataIndex;
          const bin = distributionData[dataIndex];
          return `${bin.label}<br/>Devices: ${bin.count}`;
        },
        backgroundColor: theme.palette.waltero.white,
        borderColor: theme.palette.waltero.primary.coreBlue,
        textStyle: {
          color: theme.palette.waltero.primary.coreBlue,
          fontFamily: '"Inter", sans-serif'
        }
      }
    };
  };

  // Gauge and histogram heights based on screen size
  const gaugeHeight = breakpoints.isMobile ? 180 : 220;
  const histogramHeight = breakpoints.isMobile ? 120 : 140;


  // Get connection percentages from fleetStats
  const { averageConnectionPercentage, connectionPercentages } = getConnectionPercentages();

  return (
    <Box 
      sx={{ 
        display: 'grid', 
        gridTemplateColumns: breakpoints.isMobile 
          ? '1fr' 
          : 'repeat(auto-fit, minmax(280px, 1fr))', 
        gap: breakpoints.isMobile ? 1 : 2, 
        mb: 3 
      }}
    >
      {/* Average Connection Percentage - Gauge */}
      <WalteroCard 
        variant="elevated" 
        sx={{ 
          p: 2,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Title at top */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WalteroTypography variant="h6" color="primary">
              Connection Percentage
            </WalteroTypography>
            <Tooltip 
              title="Percentage of daily connections made per sensor"
              placement="top"
              arrow
            >
              <InfoIcon 
                sx={{ 
                  fontSize: 16, 
                  color: theme.palette.grey[500],
                  cursor: 'help'
                }} 
              />
            </Tooltip>
          </Box>
          {fleetStats?.isLoading && (
            <CircularProgress size={16} sx={{ color: theme.palette.grey[500] }} />
          )}
        </Box>
        
        {/* Gauge */}
        <WalteroChart
          option={createGaugeOption(
            getNumericValue(fleetStats.averageConnectionPercentage),
            '%',
            successRateColorRanges,
            0,
            100
          )}
          height={gaugeHeight}
          loading={false}
          style={{ 
            width: '100%',
            minHeight: gaugeHeight
          }}
          aria-label={`Connection percentage gauge showing ${formatPercentage(fleetStats.averageConnectionPercentage)}`}
        />
        
        {/* Distribution Histogram */}
        <WalteroChart
          option={createHistogramOption(
            calculateDistributionData('connectionPercentage'),
            'Connection Percentage Distribution',
            theme.palette.waltero.primary.coreBlue
          )}
          height={histogramHeight}
          loading={false}
          style={{ 
            width: '100%',
            minHeight: histogramHeight,
            marginTop: theme.spacing(1)
          }}
          aria-label="Connection percentage distribution histogram"
        />
      </WalteroCard>
      
      {/* Average Success Rate - Gauge (Admin Only) */}
      {isAdmin && (
        <WalteroCard 
          variant="elevated" 
          sx={{ 
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
          }}
        >
          {/* Title at top */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WalteroTypography variant="h6" color="primary">
                Success Rate
              </WalteroTypography>
              <Tooltip 
                title="Percentage of successful data transmissions across the entire fleet"
                placement="top"
                arrow
              >
                <InfoIcon 
                  sx={{ 
                    fontSize: 16, 
                    color: theme.palette.grey[500],
                    cursor: 'help'
                  }} 
                />
              </Tooltip>
            </Box>
            {fleetStats?.isLoading && (
              <CircularProgress size={16} sx={{ color: theme.palette.grey[500] }} />
            )}
          </Box>
          
          {/* Gauge */}
          <WalteroChart
            option={createGaugeOption(
              getNumericValue(fleetStats.averageSuccessRate),
              '%',
              successRateColorRanges,
              0,
              100
            )}
            height={gaugeHeight}
            loading={false}
            style={{ 
              width: '100%',
              minHeight: gaugeHeight
            }}
            aria-label={`Success rate gauge showing ${formatPercentage(fleetStats.averageSuccessRate)}`}
          />
          
          {/* Distribution Histogram */}
          <WalteroChart
            option={createHistogramOption(
              calculateDistributionData('successRate'),
              'Success Rate Distribution',
              theme.palette.waltero.fleetStatus.excellent
            )}
            height={histogramHeight}
            loading={false}
            style={{ 
              width: '100%',
              minHeight: histogramHeight,
              marginTop: theme.spacing(1)
            }}
            aria-label="Success rate distribution histogram"
          />
          
          {/* Admin badge in lower right corner */}
          <Chip 
            label="Admin only" 
            size="small" 
            sx={{ 
              position: 'absolute',
              bottom: 8,
              right: 8,
              bgcolor: '#D32F2F', 
              color: 'white', 
              fontSize: '0.7rem',
              fontWeight: 'bold'
            }} 
          />
        </WalteroCard>
      )}
      
      {/* Average RSSI - Gauge */}
      <WalteroCard 
        variant="elevated" 
        sx={{ 
          p: 2,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Title at top */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WalteroTypography variant="h6" color="primary">
              RSSI Signal
            </WalteroTypography>
            <Tooltip 
              title="Average RSSI signal strength over the entire fleet"
              placement="top"
              arrow
            >
              <InfoIcon 
                sx={{ 
                  fontSize: 16, 
                  color: theme.palette.grey[500],
                  cursor: 'help'
                }} 
              />
            </Tooltip>
          </Box>
          {fleetStats?.isLoading && (
            <CircularProgress size={16} sx={{ color: theme.palette.grey[500] }} />
          )}
        </Box>
        
        {/* Gauge */}
        <WalteroChart
          option={createGaugeOption(
            getNumericValue(fleetStats.averageRSSI),
            '',
            rssiColorRanges,
            0,
            31
          )}
          height={gaugeHeight}
          loading={false}
          style={{ 
            width: '100%',
            minHeight: gaugeHeight
          }}
          aria-label={`RSSI signal gauge showing ${formatRSSI(fleetStats.averageRSSI)}`}
        />
        
        {/* Distribution Histogram */}
        <WalteroChart
          option={createHistogramOption(
            calculateDistributionData('rssi'),
            'RSSI Distribution',
            theme.palette.waltero.primary.accent
          )}
          height={histogramHeight}
          loading={false}
          style={{ 
            width: '100%',
            minHeight: histogramHeight,
            marginTop: theme.spacing(1)
          }}
          aria-label="RSSI signal distribution histogram"
        />
      </WalteroCard>
      
      {/* Battery Health - Gauge */}
      <WalteroCard 
        variant="elevated" 
        sx={{ 
          p: 2,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Title at top */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WalteroTypography variant="h6" color="primary">
              Battery Health
            </WalteroTypography>
            <Tooltip 
              title="Average battery voltage and distribution across sensors"
              placement="top"
              arrow
            >
              <InfoIcon 
                sx={{ 
                  fontSize: 16, 
                  color: theme.palette.grey[500],
                  cursor: 'help'
                }} 
              />
            </Tooltip>
          </Box>
          {fleetStats?.isLoading && (
            <CircularProgress size={16} sx={{ color: theme.palette.grey[500] }} />
          )}
        </Box>
        
        {/* Gauge */}
        <WalteroChart
          option={createBatteryGaugeOption(
            fleetStats.batteryHealthPercentage,
            fleetStats.averageBatteryVoltage,
            batteryColorRanges
          )}
          height={gaugeHeight}
          loading={false}
          style={{ 
            width: '100%',
            minHeight: gaugeHeight
          }}
          aria-label={`Battery health gauge showing ${formatPercentage(fleetStats.batteryHealthPercentage)}`}
        />
        
        {/* Distribution Histogram */}
        <WalteroChart
          option={createHistogramOption(
            calculateDistributionData('battery'),
            '',
            theme.palette.waltero.secondary.vibrantApricot
          )}
          height={histogramHeight}
          loading={false}
          style={{ 
            width: '100%',
            minHeight: histogramHeight,
            marginTop: theme.spacing(1)
          }}
          aria-label="Battery voltage distribution histogram"
        />
      </WalteroCard>
    </Box>
  );
};

PerformanceSummaryCards.propTypes = {
  fleetStats: PropTypes.shape({
    totalDevices: PropTypes.number,
    activeDevices: PropTypes.number,
    devicesWithData: PropTypes.number,
    averageSuccessRate: PropTypes.number,
    averageRSSI: PropTypes.number,
    batteryHealthPercentage: PropTypes.number,
    averageBatteryVoltage: PropTypes.number,
    averageConnectionPercentage: PropTypes.number,
    successRates: PropTypes.arrayOf(PropTypes.number),
    rssiValues: PropTypes.arrayOf(PropTypes.number),
    batteryVoltages: PropTypes.arrayOf(PropTypes.number),
    connectionPercentages: PropTypes.arrayOf(PropTypes.number),
    isLoading: PropTypes.bool
  }),
  performanceData: PropTypes.arrayOf(PropTypes.object)
};

PerformanceSummaryCards.defaultProps = {
  fleetStats: {},
  performanceData: []
};

export default PerformanceSummaryCards;